import dayjs from 'dayjs';
import {makeAutoObservable} from 'mobx';

import type {TimeRange, Timestamp} from '../../utils/datetime';
import type {BookingRangeLimits, CalendarStepDirection} from './types';

import {not} from '../../utils/conditionalUtils';
import {logger} from '../../utils/logger';

export class CalendarController {
  static STEP_MULT = 2;
  maxDate = dayjs()
    .add(CalendarController.STEP_MULT, 'month')
    .endOf('month')
    .valueOf();
  minDate = dayjs().startOf('hour').valueOf();
  range: TimeRange = {
    end: this.maxDate,
    start: this.minDate,
  };
  selectedDate: Timestamp = this.minDate;

  constructor() {
    makeAutoObservable(this, undefined, {
      autoBind: true,
    });
  }

  init({end, start}: BookingRangeLimits) {
    const rangeStart: Timestamp = start
      ? Math.max(start, this.minDate)
      : this.minDate;
    const rangeEnd: Timestamp = Math.min(end, this.maxDate);

    this.minDate = rangeStart;
    this.maxDate = end;
    this.setNextRange({end: rangeEnd, start: rangeStart});
  }

  onDateChange(value: Timestamp) {
    const verdict = this.dateGuard(value);

    switch (verdict) {
      case 'max_limit_error': {
        this.handleMaxLimError();
        break;
      }
      case 'min_limit_error': {
        this.handleMinLimError();
        break;
      }
      case 'range_step_back': {
        this.handleRangeError(-1, value);
        break;
      }
      case 'range_step_forward': {
        this.handleRangeError(1, value);
        break;
      }
      default: {
        this.setSelectedDate(value);
      }
    }
  }

  setNextRange(range: TimeRange) {
    this.range = {...range};
  }

  setSelectedDate(value: Timestamp) {
    this.selectedDate = value;

    logger.event('schedule_day_select_tap');
  }

  private adoptRange(nextRange: TimeRange) {
    const adopted = {...nextRange};

    if (adopted.start < this.minDate) {
      adopted.start = this.minDate;
    }

    if (adopted.end > this.maxDate) {
      adopted.end = this.maxDate;
    }

    return adopted;
  }

  private dateGuard(value: Timestamp) {
    const date = dayjs(value);

    if (date.isBefore(this.minDate)) {
      return 'min_limit_error';
    } else if (date.isAfter(this.maxDate)) {
      return 'max_limit_error';
    } else if (date.isBefore(this.range.start)) {
      return 'range_step_back';
    } else if (date.isAfter(this.range.end)) {
      return 'range_step_forward';
    } else {
      return 'ok';
    }
  }

  private handleMaxLimError() {
    const end = this.maxDate;
    const start = dayjs(end).subtract(1, 'month').startOf('month').valueOf();

    this.setNextRange({end, start});
    this.setSelectedDate(end);
  }

  private handleMinLimError() {
    const start = this.minDate;

    const endOfMonth = dayjs(start).add(1, 'month').endOf('month').valueOf();

    const end = Math.min(endOfMonth, this.maxDate);

    this.setNextRange({end, start});
    this.setSelectedDate(start);
  }

  private handleRangeError(direction: CalendarStepDirection, value: Timestamp) {
    let nextRange = this.makeNextRange(direction);
    let nextValue = value;

    const isRangeInLimits = this.rangeGuard(nextRange);

    if (!isRangeInLimits) {
      nextRange = this.adoptRange(nextRange);
      nextValue = nextRange.start;
    }

    this.setNextRange(nextRange);
    this.setSelectedDate(nextValue);
  }

  private makeNextRange(direction: CalendarStepDirection): TimeRange {
    const step = direction * CalendarController.STEP_MULT;

    const start = dayjs(this.range.start).add(step, 'month').valueOf();
    const end = dayjs(this.range.end).add(step, 'month').valueOf();

    return {end, start};
  }

  private rangeGuard(nextRange: TimeRange) {
    return not(nextRange.start < this.minDate || nextRange.end > this.maxDate);
  }
}
